import type { AlertColor } from "@mui/material/Alert/Alert";
import { AxiosError } from "axios";

import type QueryClient from "../api/query/queryClient";
import { ReviewStatus } from "../status";
import { isoNow } from "../utils/utils";

export enum PostedState {
  UNPOSTED,
  FAILURE,
  AUTO_POSTED,
  MANUALLY_POSTED,
  POSTING,
  RETRY,
  PENDING,
  REQUEUED,
}

const successStates = [
  PostedState[PostedState.AUTO_POSTED],
  PostedState[PostedState.MANUALLY_POSTED],
];

const inProgressStates = [
  PostedState[PostedState.PENDING],
  PostedState[PostedState.POSTING],
  PostedState[PostedState.REQUEUED],
];

const failureStates = [
  PostedState[PostedState.FAILURE],
  PostedState[PostedState.RETRY],
];

export function hasPosted(claim: ClaimMessage): boolean {
  return successStates.includes(claim.postedState);
}

const postableReviewStatuses: string[] = [
  ReviewStatus.ACCEPTED,
  ReviewStatus.BYPASS,
];

export const inReviewStatuses: string[] = [
  ReviewStatus.REVIEW,
  ReviewStatus.MODIFIED,
];

export function canMarkPosted(claim: ClaimMessage): boolean {
  return postableReviewStatuses.includes(claim.reviewStatus.toUpperCase());
}

export function hasFailed(
  claim: Pick<ClaimWithProcedureMessage, "postedState">,
): boolean {
  return failureStates.includes(claim.postedState);
}

const STEP_LABELS: Record<string, { landed: string; missing: string }> = {
  post_ledger: { landed: "Ledger posted", missing: "Ledger not posted" },
  payment_ledger: { landed: "Ledger posted", missing: "Ledger not posted" },
  payment_posting: { landed: "Ledger posted", missing: "Ledger not posted" },
  adjustment_posting: {
    landed: "Adjustment posted",
    missing: "Adjustment not posted",
  },
  closing_claim: { landed: "Claim closed", missing: "Claim not closed" },
  upload_document: {
    landed: "Document uploaded",
    missing: "Document not uploaded",
  },
  document_cabinet: {
    landed: "Document uploaded",
    missing: "Document not uploaded",
  },
  document_upload: {
    landed: "Document uploaded",
    missing: "Document not uploaded",
  },
  files_and_letters: {
    landed: "Document uploaded",
    missing: "Document not uploaded",
  },
  policy_notes: {
    landed: "Policy notes posted",
    missing: "Policy notes not posted",
  },
  appointment_verified: {
    landed: "Appointment verified",
    missing: "Appointment not verified",
  },
};

function landedSteps(claim: ClaimWithProcedureMessage): string[] {
  return (claim.postingCompletedSteps ?? []).filter(
    (step) => step in STEP_LABELS,
  );
}

export function unpostBlocked(
  claim: ClaimWithProcedureMessage,
  blockedPracticeIds: string[],
): boolean {
  if (!claim.practiceId || !blockedPracticeIds.includes(claim.practiceId)) {
    return false;
  }
  return hasPosted(claim) || landedSteps(claim).length > 0;
}

export function partialPosting(
  claim: ClaimWithProcedureMessage,
): { landed: string[]; missing: string[] } | null {
  if (!hasFailed(claim)) {
    return null;
  }
  const landedStepNames = landedSteps(claim);
  if (!landedStepNames.length) {
    return null;
  }
  const landed = Array.from(
    new Set(landedStepNames.map((step) => STEP_LABELS[step].landed)),
  );
  const missing = Array.from(
    new Set(
      (claim.postingExpectedSteps ?? [])
        .filter(
          (step) =>
            step in STEP_LABELS && !landed.includes(STEP_LABELS[step].landed),
        )
        .map((step) => STEP_LABELS[step].missing),
    ),
  );
  return { landed, missing };
}

export function postingStatusSuccess(postingStatus: PostingStatus): boolean {
  return successStates.includes(postingStatus.postedState);
}

export function postingStatusInProgress(postingStatus: PostingStatus): boolean {
  return inProgressStates.includes(postingStatus.postedState);
}

export function needsPolling(
  claim: ClaimWithProcedureAndPatientMessage,
): boolean {
  return [
    PostedState[PostedState.POSTING],
    PostedState[PostedState.PENDING],
    PostedState[PostedState.REQUEUED],
  ].includes(claim.postedState);
}

export function insuranceVerificationNeedsPolling(
  verification: InsuranceVerificationMessage,
): boolean {
  return [
    PostedState[PostedState.POSTING],
    PostedState[PostedState.PENDING],
    PostedState[PostedState.REQUEUED],
  ].includes(verification.postedState);
}

export async function postPMSUpdateAndNotify(
  claim: ClaimWithProcedureAndPatientMessage,
  updateStore: (action: {
    type: "UPDATE_CLAIM";
    claimToUpdate: ClaimWithProcedureAndPatientMessage;
  }) => void,
  queryClient: QueryClient,
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void,
) {
  try {
    updateStore({
      type: "UPDATE_CLAIM",
      claimToUpdate: {
        ...claim,
        postedState: PostedState[PostedState.POSTING],
        updatedAt: isoNow(),
      },
    });
    updateSnackBar({
      severity: "info",
      message: "Posting to PMS",
    });
    const postResponse = await queryClient.postClaimToPMS(claim.wieldyId);
    if (postingStatusInProgress(postResponse)) {
      updateSnackBar({
        severity: "info",
        message: `Hang tight. Claim ${claim.claimId} is posting...`,
      });
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: {
          ...claim,
          postedState: postResponse.postedState,
          postedAttempts: postResponse.postedAttempts,
          postedLatestErrorMessage: postResponse.postedLatestErrorMessage,
          updatedAt: isoNow(),
        },
      });
      return;
    }
    if (!postingStatusSuccess(postResponse)) {
      updateSnackBar({
        severity: "error",
        message:
          "There was an issue posting to PMS. Please reach out to Wieldy " +
          "support.",
      });
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: {
          ...claim,
          postedState: postResponse.postedState,
          postedAttempts: postResponse.postedAttempts,
          postedLatestErrorMessage: postResponse.postedLatestErrorMessage,
          updatedAt: isoNow(),
        },
      });
      return;
    }
    updateStore({
      type: "UPDATE_CLAIM",
      claimToUpdate: {
        ...claim,
        postedState: postResponse.postedState,
        postedDateTime: postResponse.postedDateTime,
        postedAttempts: postResponse.postedAttempts,
      },
    });
    updateSnackBar({
      severity: "success",
      message: "Successfully posted to PMS",
    });
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message: responseBody.postedLatestErrorMessage,
      });
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: {
          ...claim,
          postedState: responseBody.postedState,
          postedAttempts: responseBody.postedAttempts,
          postedLatestErrorMessage: responseBody.postedLatestErrorMessage,
        },
      });
    }
  }
}

export async function postInsuranceVerificationPMSUpdateAndNotify(
  verification: InsuranceVerificationMessage,
  updateStore: (action: {
    type: "VERIFICATION_POSTED_STATUS_CHANGED";
    verificationToUpdate: InsuranceVerificationMessage;
  }) => void,
  queryClient: QueryClient,
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void,
) {
  try {
    updateStore({
      type: "VERIFICATION_POSTED_STATUS_CHANGED",
      verificationToUpdate: {
        ...verification,
        postedState: PostedState[PostedState.POSTING],
      },
    });
    updateSnackBar({
      severity: "info",
      message: "Posting insurance verification to PMS",
    });
    const postResponse = await queryClient.postInsuranceVerificationToPMS(
      verification.wieldyId,
    );
    if (postingStatusInProgress(postResponse)) {
      updateSnackBar({
        severity: "info",
        message: `Hang tight. Insurance verification ${verification.wieldyId} is posting...`,
      });
      updateStore({
        type: "VERIFICATION_POSTED_STATUS_CHANGED",
        verificationToUpdate: {
          ...verification,
          postedState: postResponse.postedState,
          postedAttempts: postResponse.postedAttempts,
          postedLatestErrorMessage: postResponse.postedLatestErrorMessage,
        },
      });
      return;
    }
    if (!postingStatusSuccess(postResponse)) {
      updateSnackBar({
        severity: "error",
        message:
          "There was an issue posting insurance verification to PMS. Please reach out to Wieldy " +
          "support.",
      });
      updateStore({
        type: "VERIFICATION_POSTED_STATUS_CHANGED",
        verificationToUpdate: {
          ...verification,
          postedState: postResponse.postedState,
          postedAttempts: postResponse.postedAttempts,
          postedLatestErrorMessage: postResponse.postedLatestErrorMessage,
        },
      });
      return;
    }
    updateStore({
      type: "VERIFICATION_POSTED_STATUS_CHANGED",
      verificationToUpdate: {
        ...verification,
        postedState: postResponse.postedState,
        postedDateTime: postResponse.postedDateTime,
        postedAttempts: postResponse.postedAttempts,
      },
    });
    updateSnackBar({
      severity: "success",
      message: "Successfully posted insurance verification to PMS",
    });
  } catch (error: unknown) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseBody = error.response.data;
      updateSnackBar({
        severity: "error",
        message: responseBody.postedLatestErrorMessage,
      });
      updateStore({
        type: "VERIFICATION_POSTED_STATUS_CHANGED",
        verificationToUpdate: {
          ...verification,
          postedState: responseBody.postedState,
          postedAttempts: responseBody.postedAttempts,
          postedLatestErrorMessage: responseBody.postedLatestErrorMessage,
        },
      });
    }
  }
}
